import {Controller} from '@hotwired/stimulus';

// ce que chaque aperçu affiche, en plus du titre (qui retombe toujours sur le titre de la page)
const NEEDS = {
  google: ['description'],
  facebook: ['image', 'description'],
  twitter: ['image', 'description'],
  linkedin: ['image'],
};

/**
 * Aperçus du Référencement (« Modifier la page ») : l'image des aperçus est l'image d'illustration de la langue
 * affichée, et un aperçu à qui il manque une information qu'il affiche (image d'illustration, meta description) cède
 * la place à une alerte qui dit quoi renseigner, avec un lien vers chaque champ manquant. L'aperçu revient dès que
 * tout est renseigné.
 *
 * Targets : `card` et `missing` (`data-network` : google, facebook, twitter, linkedin), `media` (fond des aperçus
 * Facebook, X et LinkedIn). Values : `images` (URL de l'image d'illustration enregistrée, par langue),
 * `descriptionField` (id du champ meta description, `{lang}` pour la langue), `labels` (`networks` par réseau,
 * `both`, `image`, `description`, `imageLink`, `descriptionLink`).
 */
export default class extends Controller {
  static targets = ['card', 'missing', 'media'];

  static values = {
    images: Object,
    descriptionField: String,
    labels: Object,
  };

  connect() {
    this.images = {...this.imagesValue};
    this.refresh();
  }

  imageChanged(event) {
    const version = event.target.closest('[class*="version_"]');
    const lang = version ? /version_(\w+)/.exec(version.className)[1] : this.#lang();

    this.images[lang] = event.detail.url;
    this.refresh();
  }

  refresh() {
    const lang = this.#lang();
    const image = this.images[lang] ?? '';
    const description = document.getElementById(this.descriptionFieldValue.replace('{lang}', lang));
    const filled = {
      image: image !== '',
      description: description !== null && description.value.trim() !== '',
    };

    this.mediaTargets.forEach((media) => {
      media.style.backgroundImage = image === '' ? '' : `url("${image}")`;
    });

    this.cardTargets.forEach((card) => {
      const missing = NEEDS[card.dataset.network].filter((need) => !filled[need]);
      const alert = this.missingTargets.find((target) => target.dataset.network === card.dataset.network);

      card.hidden = missing.length > 0;
      alert.hidden = missing.length === 0;

      if (missing.length > 0) {
        this.#write(alert.querySelector('.alert-ds-content'), card.dataset.network, missing, lang);
      }
    });
  }

  // « Pour afficher l'aperçu Facebook, renseignez l'{image} et la {description} de la page. », les deux en liens
  #write(container, network, missing, lang) {
    const key = missing.length === 2 ? 'both' : missing[0];
    const sentence = this.labelsValue[key].replace('{network}', () => this.labelsValue.networks[network]);

    container.replaceChildren(...sentence.split(/(\{image\}|\{description\})/).map((part) => {
      if (part === '{image}') {
        return this.#link(this.labelsValue.imageLink, () => this.#chooseImage(lang));
      }

      if (part === '{description}') {
        return this.#link(this.labelsValue.descriptionLink, () => {
          document.getElementById(this.descriptionFieldValue.replace('{lang}', lang))?.focus();
        });
      }

      return document.createTextNode(part);
    }));
  }

  #link(text, onClick) {
    const link = document.createElement('a');
    link.href = '#';
    link.textContent = text;
    link.addEventListener('click', (event) => {
      event.preventDefault();
      onClick();
    });

    return link;
  }

  // la médiathèque du cadre « Image d'illustration » de la langue affichée, comme un clic sur le cadre vide
  #chooseImage(lang) {
    document.querySelector(`.media-frame.version_${lang} .media-frame-choose`)?.click();
  }

  #lang() {
    return document.querySelector('.lang-tabs .nav-link.active')?.dataset.lang ?? Object.keys(this.images)[0];
  }
}
